import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SignatureCapture } from '@/components/documents/SignatureCapture';
import { generateDocumentPdf } from '@/lib/pdfGeneration';
import { VideoPlayer } from './VideoPlayer';
import { CompletedDocumentViewer } from './CompletedDocumentViewer';
import { toast } from 'sonner';
import { Loader2, FileText, PenTool, BookOpen, ChevronRight, ChevronLeft, Users, GraduationCap, Heart, CheckCircle2, Shield, Award } from 'lucide-react';
import { format } from 'date-fns';
import { sanitizeDocumentContent } from '@/lib/sanitize';

// Default video URL fallback
const DEFAULT_VIDEO_URL = 'https://vimeo.com/1091482323';

interface FormField {
  field_name: string;
  field_label: string;
  field_type: 'text' | 'textarea' | 'select';
  is_required: boolean;
  placeholder?: string;
  field_options?: string[];
  icon?: string;
  store_globally?: boolean;
}

interface IntroConfig {
  icon?: string;
  title?: string;
  text?: string;
}

interface JourneyMiniWorkflowStepProps {
  stepId: string;
  userId: string;
  templateName: string;
  documentTitle: string;
  documentType: string;
  documentCategory: string;
  formFields: FormField[];
  isCompleted: boolean;
  existingData?: Record<string, any>;
  onComplete: () => void;
  videoUrl?: string;
  introConfig?: IntroConfig;
  completedDocumentId?: string;
  storeFormDataGlobally?: boolean;
  initialProgress?: number;
}

interface UnaData {
  id: string;
  name: string;
  treasury_secretary_id: string;
  primary_officer_role: string | null;
}

interface ProfileData {
  full_name: string;
}

type WorkflowPhase = 'form' | 'preview' | 'signing' | 'filing' | 'complete';

// Icon mapping for dynamic icon rendering
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  heart: Heart,
  shield: Shield,
  'book-open': BookOpen,
  users: Users,
  award: Award,
  'graduation-cap': GraduationCap,
};

export function JourneyMiniWorkflowStep({
  stepId,
  userId,
  templateName,
  documentTitle,
  documentType,
  documentCategory,
  formFields,
  isCompleted,
  existingData,
  onComplete,
  videoUrl,
  introConfig,
  completedDocumentId,
  storeFormDataGlobally,
  initialProgress = 0,
}: JourneyMiniWorkflowStepProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [unaData, setUnaData] = useState<UnaData | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [templateContent, setTemplateContent] = useState<string>('');
  const [formData, setFormData] = useState<Record<string, string>>(existingData || {});
  const [showSignature, setShowSignature] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<WorkflowPhase>('form');
  const [processingStep, setProcessingStep] = useState<number>(0);
  
  // Video tracking state
  const [videoWatchedPercent, setVideoWatchedPercent] = useState(initialProgress);
  const [videoCompleted, setVideoCompleted] = useState(initialProgress >= 90);
  const watchedPercentRef = useRef(initialProgress);
  const lastSavedPercentRef = useRef(Math.floor(initialProgress / 5));
  const isCompletedRef = useRef(false);

  // Use provided video URL or fallback to default
  const effectiveVideoUrl = videoUrl || DEFAULT_VIDEO_URL;

  // Update ref when progress changes
  useEffect(() => {
    watchedPercentRef.current = videoWatchedPercent;
  }, [videoWatchedPercent]);

  // Save progress on component unmount
  useEffect(() => {
    return () => {
      // Don't save progress if step was just completed - avoid race condition
      if (isCompletedRef.current) return;
      
      const currentProgress = watchedPercentRef.current;
      if (currentProgress > 0 && currentProgress > initialProgress) {
        supabase
          .from('user_journey_step_progress')
          .upsert({
            user_id: userId,
            step_id: stepId,
            status: 'active',
            action_data: { watch_percent: currentProgress },
          }, { onConflict: 'user_id,step_id' })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
          });
      }
    };
  }, [userId, stepId, initialProgress, queryClient]);

  const handleVideoProgress = useCallback(async (percent: number) => {
    watchedPercentRef.current = percent;
    setVideoWatchedPercent(percent);
    
    const currentInterval = Math.floor(percent / 5);
    if (currentInterval > lastSavedPercentRef.current) {
      lastSavedPercentRef.current = currentInterval;
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'active',
          action_data: { watch_percent: percent },
        }, { onConflict: 'user_id,step_id' });
      
      queryClient.invalidateQueries({ queryKey: ['journey-steps-progress'] });
    }
  }, [userId, stepId, queryClient]);

  const handleVideoComplete = useCallback(() => {
    setVideoCompleted(true);
  }, []);

  // Get icon component for a field
  const getFieldIcon = (iconName?: string) => {
    if (!iconName) return null;
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? <IconComponent className="w-4 h-4 text-primary" /> : null;
  };

  // Fetch UNA data, profile, template, and onboarding data for prepopulation
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user's UNA
        const { data: una, error: unaError } = await supabase
          .from('unas')
          .select('id, name, treasury_secretary_id, primary_officer_role')
          .eq('treasury_secretary_id', userId)
          .maybeSingle();

        if (unaError) throw unaError;
        if (!una) {
          toast.error('UNA not found. Please complete previous steps first.');
          return;
        }
        setUnaData(una);

        // Fetch user profile (including address fields - the source of truth)
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('full_name, address, city, state, zip, country')
          .eq('id', userId)
          .single();

        if (profileError) throw profileError;
        setProfileData(profile);

        // Fetch document template
        const { data: template, error: templateError } = await supabase
          .from('document_templates')
          .select('template_content')
          .eq('template_name', templateName)
          .eq('is_active', true)
          .single();

        if (templateError) throw templateError;
        setTemplateContent(template.template_content);

        // Priority for address prepopulation:
        // 1. Existing form data (user already edited)
        // 2. profiles.address (user edited in Settings or synced from OntraPort)
        // 3. onboarding_data.legal_address (from OntraPort fulfillment progress - fallback)
        
        if (!existingData) {
          const prepopulatedData: Record<string, string> = {};

          // Check if profile has address (source of truth)
          if (profile?.address) {
            prepopulatedData.place_of_worship_address = profile.address;
            
            // Combine city, state, zip from profile
            const cityStateParts = [
              profile.city,
              profile.state,
              profile.zip
            ].filter(Boolean);
            
            if (cityStateParts.length > 0) {
              let cityStateZip = cityStateParts.join(', ');
              if (profile.country && profile.country !== 'US') {
                cityStateZip += ', ' + profile.country;
              }
              prepopulatedData.place_of_worship_city_state_zip = cityStateZip;
            }
          } else {
            // Fallback to onboarding data from fulfillment progress
            const { data: fulfillmentData } = await supabase
              .from('user_fulfillment_progress')
              .select('onboarding_data')
              .eq('user_id', userId)
              .maybeSingle();

            if (fulfillmentData?.onboarding_data) {
              const onboardingData = fulfillmentData.onboarding_data as Record<string, any>;

              // Map address fields from OntraPort data
              if (onboardingData.legal_address) {
                prepopulatedData.place_of_worship_address = onboardingData.legal_address;
              }

              // Combine city, state, zip into one field
              const cityStateParts = [
                onboardingData.legal_city,
                onboardingData.legal_state,
                onboardingData.legal_zip
              ].filter(Boolean);
              
              if (cityStateParts.length > 0) {
                prepopulatedData.place_of_worship_city_state_zip = cityStateParts.join(', ');
              }
            }
          }

          // Only set if we have prepopulated data
          if (Object.keys(prepopulatedData).length > 0) {
            setFormData(prev => ({ ...prepopulatedData, ...prev }));
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load step data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, templateName, existingData]);

  const handleFieldChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isFormValid = () => {
    return formFields
      .filter(f => f.is_required)
      .every(f => formData[f.field_name]?.trim());
  };

  // Generate document HTML with merge fields - DYNAMIC replacement
  const getPopulatedHtml = (): string => {
    if (!templateContent || !unaData || !profileData) return '';

    let html = templateContent;
    
    // Replace standard merge fields
    html = html.replace(/\{\{una_name\}\}/g, unaData.name || '');
    html = html.replace(/\{\{signer_name\}\}/g, profileData.full_name || '');
    html = html.replace(/\{\{signature_date\}\}/g, format(new Date(), 'MMMM d, yyyy'));

    // Dynamically replace ALL form data merge fields
    Object.entries(formData).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      html = html.replace(regex, value || '');
    });

    return html;
  };

  // Wrap HTML in full ecclesiastical document styling for PDF generation
  const wrapInEcclesiasticalStyles = (bodyHtml: string): string => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
    }
    
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      background: white;
      margin: 0;
      padding: 0;
    }
    
    h1 { 
      font-size: 18pt; 
      font-weight: bold; 
      margin: 0 0 20pt 0;
      text-align: center;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 14pt;
    }
    
    h2 { 
      font-size: 13pt; 
      font-weight: bold; 
      margin: 24pt 0 10pt 0;
      border-bottom: 1px solid #bfbfbf;
      padding-bottom: 6pt;
      color: #262626;
    }
    
    h3 { 
      font-size: 12pt; 
      font-weight: bold; 
      font-style: italic;
      margin: 18pt 0 8pt 0;
      color: #333;
    }
    
    p { 
      margin: 0 0 14pt 0;
      font-size: 11pt;
      text-align: justify;
    }
    
    li { margin: 8pt 0; }
    ul, ol { margin: 14pt 0; padding-left: 28pt; }
    
    .signature-section {
      margin-top: 40pt;
      padding-top: 24pt;
      border-top: 1px solid #ccc;
    }
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
  };

  const handleContinueToPreview = () => {
    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCurrentPhase('preview');
  };

  // Handle signature capture
  const handleSignature = async (signatureData: string) => {
    if (!unaData || !profileData) return;

    setProcessing(true);
    setShowSignature(false);
    setCurrentPhase('signing');
    setProcessingStep(1);
    // Prevent cleanup from overwriting completion
    isCompletedRef.current = true;

    try {
      // 1. Embed signature into HTML
      const populatedHtml = getPopulatedHtml();
      const signedHtml = embedSignatureInHtml(populatedHtml, signatureData);
      const styledSignedHtml = wrapInEcclesiasticalStyles(signedHtml);
      setProcessingStep(2);

      // 2. Create generated_documents record
      const { data: docRecord, error: docError } = await supabase
        .from('generated_documents')
        .insert({
          una_id: unaData.id,
          document_name: documentTitle,
          document_type: documentType,
          is_custom: true,
          custom_content: styledSignedHtml,
          status: 'finalized',
          created_by: userId,
          finalized_by: userId,
          version: 1,
          merge_data: {
            una_name: unaData.name,
            signer_name: profileData.full_name,
            signature_date: format(new Date(), 'yyyy-MM-dd'),
            ...formData,
          },
          finalized_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (docError) throw docError;
      setProcessingStep(3);

      // 3. Generate and upload PDF
      setCurrentPhase('filing');
      try {
        const pdfResult = await generateDocumentPdf(docRecord.id, documentTitle);

        // Update document with file URL
        await supabase
          .from('generated_documents')
          .update({
            file_url: pdfResult.file_url,
            file_path: pdfResult.file_path,
            file_size: pdfResult.file_size,
          })
          .eq('id', docRecord.id);
      } catch (pdfError) {
        console.warn('PDF generation failed, continuing without PDF:', pdfError);
      }
      setProcessingStep(4);

      // 4. File to governance_documents
      const { error: govError } = await supabase
        .from('governance_documents')
        .insert({
          una_id: unaData.id,
          document_type: documentType,
          document_category: documentCategory,
          document_title: documentTitle,
          original_file_url: docRecord.file_url || '',
          generated_from_task_id: null,
          effective_date: new Date().toISOString().split('T')[0],
          access_level: 'all_members',
          created_by: userId,
        });

      if (govError) throw govError;
      setProcessingStep(5);

      // 5. Create journal entry
      const officerRole = unaData.primary_officer_role || 'Secretary Treasurer';
      const { error: journalError } = await supabase
        .from('una_journal_records' as any)
        .insert({
          una_id: unaData.id,
          record_type: 'General Record',
          record_title: `${documentTitle} Signed`,
          record_date: new Date().toISOString().split('T')[0],
          activity_description: `${officerRole} ${profileData.full_name} signed the ${documentTitle}, committing to share stewardship principles with others and train the next generation for ${unaData.name}.`,
          activity_category: 'Governance',
          generated_document_id: docRecord.id,
          authorized_by: profileData.full_name,
          created_by: userId,
        } as any);

      if (journalError) throw journalError;
      setProcessingStep(6);

      // 6. Mark journey step complete
      await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          action_data: {
            document_id: docRecord.id,
            signed_at: new Date().toISOString(),
            form_data: formData,
          },
        }, { onConflict: 'user_id,step_id' });

      // 6.5. Store form data globally if configured
      if (storeFormDataGlobally) {
        try {
          // Build global data object from fields marked store_globally
          const globalData: Record<string, string> = {};
          formFields.forEach(field => {
            if (field.store_globally && formData[field.field_name]) {
              globalData[field.field_name] = formData[field.field_name];
            }
          });

          // If address fields were edited, also update the profiles table (source of truth)
          if (formData.place_of_worship_address || formData.place_of_worship_city_state_zip) {
            const addressUpdate: Record<string, string> = {};
            
            if (formData.place_of_worship_address) {
              addressUpdate.address = formData.place_of_worship_address;
            }
            
            // Parse city_state_zip if provided
            if (formData.place_of_worship_city_state_zip) {
              const parts = formData.place_of_worship_city_state_zip.split(',').map(p => p.trim());
              if (parts.length >= 1) addressUpdate.city = parts[0];
              if (parts.length >= 2) addressUpdate.state = parts[1];
              if (parts.length >= 3) addressUpdate.zip = parts[2];
            }

            if (Object.keys(addressUpdate).length > 0) {
              await supabase
                .from('profiles')
                .update(addressUpdate)
                .eq('id', userId);
              console.log('Profile address updated:', addressUpdate);
            }
          }

          // Fetch existing journey_form_data and merge
          const { data: existingState } = await supabase
            .from('user_journey_state')
            .select('journey_form_data')
            .eq('user_id', userId)
            .single();

          const existingFormData = (existingState?.journey_form_data || {}) as Record<string, any>;
          
          // Store in the expected structure for place_of_worship AND flat keys
          const updatedFormData = {
            ...existingFormData,
            place_of_worship: {
              ...(existingFormData.place_of_worship || {}),
              address: formData.place_of_worship_address || existingFormData.place_of_worship?.address || '',
              city_state_zip: formData.place_of_worship_city_state_zip || existingFormData.place_of_worship?.city_state_zip || '',
              description: formData.place_description || existingFormData.place_of_worship?.description || '',
            },
            // Also store flat for direct access by resolutions
            place_of_worship_address: formData.place_of_worship_address || existingFormData.place_of_worship_address || '',
            place_of_worship_city_state_zip: formData.place_of_worship_city_state_zip || existingFormData.place_of_worship_city_state_zip || '',
          };

          await supabase
            .from('user_journey_state')
            .upsert({
              user_id: userId,
              journey_form_data: updatedFormData,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });

          console.log('Global form data saved:', updatedFormData);
        } catch (globalDataError) {
          console.warn('Failed to save global form data:', globalDataError);
          // Don't fail the whole operation if global save fails
        }
      }

      // 7. Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      setCurrentPhase('complete');
      toast.success(`${documentTitle} signed and filed successfully!`);
      onComplete();
    } catch (error) {
      console.error('Error processing signature:', error);
      toast.error('Failed to process signature. Please try again.');
      setCurrentPhase('preview');
      setProcessingStep(0);
    } finally {
      setProcessing(false);
    }
  };

  // Embed signature into HTML
  const embedSignatureInHtml = (html: string, signatureBase64: string): string => {
    const signatureHtml = `
      <div style="margin-top: 10px;">
        <img src="${signatureBase64}" alt="Signature" style="max-height: 60px; max-width: 300px;" />
        <p style="font-size: 10pt; color: #666; margin-top: 4px;">
          Electronically signed on ${format(new Date(), 'MMMM d, yyyy at h:mm a')}
        </p>
      </div>
    `;

    // Replace the signature placeholder
    return html.replace(
      /<div[^>]*class="signature-placeholder"[^>]*data-signer-id="treasury_secretary"[^>]*><\/div>/,
      signatureHtml
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="space-y-6">
        {/* Keep video available for replay after completion */}
        {effectiveVideoUrl && (
          <div className="relative">
            <VideoPlayer
              videoUrl={effectiveVideoUrl}
              requiredWatchPercent={0}
              onProgress={() => {}}
              onComplete={() => {}}
              initialProgress={0}
            />
            <div className="absolute top-3 right-3 bg-primary/90 text-primary-foreground px-3 py-1 rounded-full flex items-center gap-1.5 text-sm font-medium shadow-lg pointer-events-none">
              <CheckCircle2 className="w-4 h-4" />
              <span>Completed</span>
            </div>
          </div>
        )}

        <CompletedDocumentViewer
          documentId={completedDocumentId || ''}
          documentTitle={documentTitle}
          successMessage="Your document has been recorded and filed to UNA Governance."
        />
      </div>
    );
  }

  if (!unaData || !profileData) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-amber-800">
          Unable to load your UNA data. Please ensure previous steps are completed.
        </p>
      </div>
    );
  }

  // Get intro icon component
  const IntroIcon = introConfig?.icon ? ICON_MAP[introConfig.icon] : Heart;

  // Phase 1: Form Collection (with required video)
  if (currentPhase === 'form') {
    return (
      <div className="space-y-6">
        {/* Video Section - Must be watched first */}
        <div className="space-y-3">
          <h3 className="font-semibold text-lg">Watch This Important Video</h3>
          <VideoPlayer
            videoUrl={effectiveVideoUrl}
            requiredWatchPercent={95}
            onProgress={handleVideoProgress}
            onComplete={handleVideoComplete}
            initialProgress={initialProgress}
          />
          {videoCompleted ? (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              <span>Video completed - you may now fill out the form below</span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Please watch the video above before completing the form ({Math.round(videoWatchedPercent)}% watched)
            </p>
          )}
        </div>

        {/* Introduction - Dynamic based on introConfig */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            {IntroIcon && <IntroIcon className="w-5 h-5 text-blue-600 mt-0.5" />}
            <div>
              <p className="font-medium text-blue-800 mb-1">
                {introConfig?.title || 'Complete Your Declaration'}
              </p>
              <p className="text-sm text-blue-700">
                {introConfig?.text || 'Your answers go straight into your document.'}
              </p>
            </div>
          </div>
        </div>

        {/* Form Fields - Dynamic icons from field config */}
        <div className="space-y-6">
          {formFields.map((field) => (
            <div key={field.field_name} className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                {getFieldIcon(field.icon)}
                <Label htmlFor={field.field_name} className="text-base font-medium">
                  {field.field_label}
                  {field.is_required && <span className="text-destructive ml-1">*</span>}
                </Label>
              </div>

              <Textarea
                id={field.field_name}
                value={formData[field.field_name] || ''}
                onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
                placeholder={field.placeholder}
                rows={4}
                className="resize-none"
                disabled={!videoCompleted}
              />
            </div>
          ))}
        </div>

        {/* Continue Button - requires both video AND form */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleContinueToPreview}
            disabled={!isFormValid() || !videoCompleted}
            size="lg"
          >
            Continue to Document Review
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Phase 2: Document Preview
  if (currentPhase === 'preview') {
    return (
      <div className="space-y-6">
        {/* Progress Indicator */}
        {processing && (
          <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <div>
              <p className="font-medium text-blue-800">Processing your signature...</p>
            </div>
          </div>
        )}

        {/* Document Preview */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">{documentTitle}</h3>
            </div>
            
            <div 
              className="border rounded-lg p-6 bg-white max-h-[400px] overflow-y-auto ecclesiastical-document"
              dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(getPopulatedHtml()) }}
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setCurrentPhase('form')}
              variant="outline"
              size="lg"
              disabled={processing}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Edit
            </Button>
            <Button
              onClick={() => setShowSignature(true)}
              disabled={processing}
              size="lg"
              className="flex-1"
            >
              <PenTool className="w-4 h-4 mr-2" />
              Sign & Commit
            </Button>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
            <BookOpen className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">What happens when you sign:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Your signature will be embedded in the document</li>
                <li>The signed document will be filed to UNA Governance</li>
                <li>A journal entry will record your commitment</li>
                <li>You'll move to the next step in your journey</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Signature Modal */}
        {showSignature && (
          <SignatureCapture
            role={unaData.primary_officer_role || "treasury_secretary"}
            signerName={profileData.full_name}
            onSave={handleSignature}
            onCancel={() => setShowSignature(false)}
            isLoading={processing}
          />
        )}
      </div>
    );
  }

  // Phase 3 & 4: Processing States (signing/filing)
  if (currentPhase === 'signing' || currentPhase === 'filing') {
    const processingSteps = [
      { label: 'Embedding signature', step: 1 },
      { label: 'Creating document record', step: 2 },
      { label: 'Generating PDF', step: 3 },
      { label: 'Filing to governance', step: 4 },
      { label: 'Recording in journal', step: 5 },
      { label: 'Finalizing your step', step: 6 },
    ];

    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="text-center space-y-6">
            {/* Animated spinner with checkmark */}
            <div className="flex justify-center">
              <div className="relative">
                <Loader2 className="w-12 h-12 animate-spin text-primary" />
                <div className="absolute -right-1 -bottom-1 bg-background rounded-full">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </div>
            
            {/* Current step message */}
            <div>
              <h3 className="font-semibold text-lg mb-2">Processing Your Signature</h3>
              <p className="text-muted-foreground">
                {processingSteps.find(s => s.step === processingStep)?.label || 'Initializing...'}
              </p>
            </div>
            
            {/* Progress steps checklist */}
            <div className="bg-background rounded-lg p-4 max-w-sm mx-auto">
              <div className="space-y-3 text-left">
                {processingSteps.map((item) => (
                  <div key={item.step} className="flex items-center gap-3">
                    {processingStep > item.step ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : processingStep === item.step ? (
                      <Loader2 className="w-5 h-5 animate-spin text-primary flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-muted flex-shrink-0" />
                    )}
                    <span className={
                      processingStep > item.step 
                        ? 'text-muted-foreground line-through' 
                        : processingStep === item.step 
                          ? 'font-medium text-foreground' 
                          : 'text-muted-foreground'
                    }>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Please wait while we finalize your commitment...
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
}
